import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Badge, Modal, EmptyState, LoadingSkeleton, ToastContainer } from './ui';
import { HeartIcon, PlusIcon, UserPlusIcon, UsersIcon } from '@heroicons/react/24/outline';
import { safeErrorMessage } from '../utils/apiError';
import { hasAnyRole } from '../utils/roles';

const categories = ['academic', 'cultural', 'religious', 'sports', 'arts', 'technology', 'community'];

const CREATE_ROLES = ['administrator', 'principal'];

function Clubs({ token, user }) {
  const canCreate = hasAnyRole(user, CREATE_ROLES);
  const [clubs, setClubs] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [selectedClub, setSelectedClub] = useState(null);
  const [clubMembers, setClubMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'success') => setToasts(prev => [...prev, { message, type }]), []);
  const removeToast = (index) => setToasts(prev => prev.filter((_, i) => i !== index));
  const [formData, setFormData] = useState({
    name: '', category: '', patron_name: '', meeting_day: '', meeting_time: '', venue: '', description: ''
  });
  const [registerData, setRegisterData] = useState({ student_id: '', role: 'member' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [clubsRes, studentsRes] = await Promise.all([
        fetch('/api/v1/clubs', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/v1/students', { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      setClubs(await clubsRes.json());
      setStudents(await studentsRes.json());
    } catch {
      addToast('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  }, [token, addToast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchClubMembers = async (clubId) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/clubs/${clubId}/members`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setClubMembers(response.ok ? await response.json() : []);
    } catch {
      addToast('Failed to load club members', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/v1/clubs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setShowForm(false);
        fetchData();
        setFormData({ name: '', category: '', patron_name: '', meeting_day: '', meeting_time: '', venue: '', description: '' });
      } else {
        addToast(await safeErrorMessage(response, 'Failed to create club'), 'error');
      }
    } catch {
      addToast('Network error. Please check your connection and try again.', 'error');
    }
  };

  const handleRegister = async (e, clubId) => {
    e.preventDefault();
    if (!registerData.student_id) { addToast('Please select a student', 'error'); return; }
    try {
      const response = await fetch(`/api/v1/clubs/${clubId}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(registerData)
      });
      if (response.ok) {
        setShowRegister(false);
        setSelectedClub(null);
        setRegisterData({ student_id: '', role: 'member' });
        setSearchTerm('');
        if (showMembers && selectedClub) fetchClubMembers(selectedClub.id);
      } else {
        addToast(await safeErrorMessage(response, 'Failed to register student'), 'error');
      }
    } catch {
      addToast('Network error. Please check your connection and try again.', 'error');
    }
  };

  const handleRemoveMember = async (clubId, studentId, studentName) => {
    if (!window.confirm(`Remove ${studentName}?`)) return;
    try {
      const response = await fetch(`/api/v1/clubs/${clubId}/members/${studentId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) { fetchClubMembers(clubId); fetchData(); }
      else addToast('Failed to remove member', 'error');
    } catch {
      addToast('Error removing member', 'error');
    }
  };

  const filteredStudents = students.filter(s =>
    s.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.class_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const categoryBadge = (cat) => {
    const colors = { academic: 'primary', cultural: 'info', sports: 'danger', arts: 'warning', technology: 'default', religious: 'info', community: 'success' };
    return <Badge variant={colors[cat] || 'default'}>{cat}</Badge>;
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)' }}>Clubs & Societies</h2>
        {canCreate && (
          <Button variant="primary" size="sm" onClick={() => setShowForm(!showForm)}>
            <PlusIcon style={{ width: 16, height: 16 }} />
            {showForm ? 'Cancel' : 'Create Club'}
          </Button>
        )}
      </div>

      <Modal isOpen={canCreate && showForm} onClose={() => setShowForm(false)} title="Create New Club">
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Club Name *</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
            </div>
            <div className="form-group">
              <label>Category</label>
              <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})}>
                <option value="">Select Category</option>
                {categories.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Patron/Teacher</label>
              <input type="text" value={formData.patron_name} onChange={(e) => setFormData({...formData, patron_name: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Meeting Day</label>
              <select value={formData.meeting_day} onChange={(e) => setFormData({...formData, meeting_day: e.target.value})}>
                <option value="">Select Day</option>
                {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Meeting Time</label>
              <input type="time" value={formData.meeting_time} onChange={(e) => setFormData({...formData, meeting_time: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Venue</label>
              <input type="text" value={formData.venue} onChange={(e) => setFormData({...formData, venue: e.target.value})} />
            </div>
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows="3" />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button type="submit" variant="primary">Create Club</Button>
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </form>
      </Modal>

      {loading && !showMembers ? (
        <LoadingSkeleton type="card" rows={4} />
      ) : clubs.length === 0 ? (
        <EmptyState
          title="No clubs yet"
          message={canCreate ? 'Create your first club to get started.' : 'No clubs to show yet.'}
          icon={<HeartIcon />}
          action={canCreate && <Button variant="primary" size="sm" onClick={() => setShowForm(true)}><PlusIcon style={{ width: 16, height: 16 }} />Create Club</Button>}
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1rem' }}>
          {clubs.map(club => (
            <Card key={club.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600 }}>{club.name}</h3>
                {club.category && categoryBadge(club.category)}
              </div>
              {club.patron_name && <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}><strong>Patron:</strong> {club.patron_name}</p>}
              {club.meeting_day && <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}><strong>Meeting:</strong> {club.meeting_day} at {club.meeting_time}</p>}
              {club.venue && <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}><strong>Venue:</strong> {club.venue}</p>}
              {club.description && <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: '0.5rem' }}>{club.description}</p>}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <Button variant="primary" size="sm" onClick={() => { setSelectedClub(club); setShowRegister(true); setSearchTerm(''); }}>
                  <UserPlusIcon style={{ width: 14, height: 14 }} />
                  Register
                </Button>
                <Button variant="secondary" size="sm" onClick={() => { setSelectedClub(club); fetchClubMembers(club.id); setShowMembers(true); }}>
                  <UsersIcon style={{ width: 14, height: 14 }} />
                  Members
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal isOpen={showRegister && !!selectedClub} onClose={() => { setShowRegister(false); setSelectedClub(null); }} title={`Register for ${selectedClub?.name || ''}`}>
        <form onSubmit={(e) => handleRegister(e, selectedClub?.id)}>
          <div className="form-group">
            <label>Search Student</label>
            <input type="text" placeholder="Type to search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} autoFocus />
          </div>
          <div className="form-group">
            <label>Select Student *</label>
            <select value={registerData.student_id} onChange={(e) => setRegisterData({...registerData, student_id: e.target.value})} required
              style={{ minHeight: '120px' }} size={5}>
              <option value="">Choose...</option>
              {filteredStudents.map(s => <option key={s.id} value={s.id}>{s.full_name} - {s.class_name || 'No Class'}</option>)}
            </select>
            {filteredStudents.length === 0 && searchTerm && <small style={{ color: 'var(--color-text-muted)' }}>No matches</small>}
          </div>
          <div className="form-group">
            <label>Role</label>
            <select value={registerData.role} onChange={(e) => setRegisterData({...registerData, role: e.target.value})}>
              <option value="member">Member</option>
              <option value="chairperson">Chairperson</option>
              <option value="secretary">Secretary</option>
              <option value="treasurer">Treasurer</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button type="submit" variant="primary">Register</Button>
            <Button type="button" variant="secondary" onClick={() => { setShowRegister(false); setSelectedClub(null); }}>Cancel</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={showMembers && !!selectedClub} onClose={() => { setShowMembers(false); setSelectedClub(null); setClubMembers([]); }} title={`${selectedClub?.name || ''} — Members (${clubMembers.length})`} width="600px">
        {loading ? (
          <LoadingSkeleton type="text" rows={5} />
        ) : clubMembers.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: '2rem' }}>No members yet.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ background: 'var(--color-bg)' }}>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600 }}>Student</th>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600 }}>Role</th>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600 }}>Joined</th>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clubMembers.map(member => (
                  <tr key={member.id} style={{ borderTop: '1px solid var(--color-border-light)' }}>
                    <td style={{ padding: '0.5rem 0.75rem' }}>{member.student_name}</td>
                    <td style={{ padding: '0.5rem 0.75rem' }}><Badge variant="info">{member.role?.toUpperCase() || 'MEMBER'}</Badge></td>
                    <td style={{ padding: '0.5rem 0.75rem' }}>{new Date(member.join_date).toLocaleDateString()}</td>
                    <td style={{ padding: '0.5rem 0.75rem' }}>
                      <Button variant="danger" size="sm" onClick={() => handleRemoveMember(selectedClub?.id, member.student_id, member.student_name)}>
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
}

export default Clubs;
